import React, {
  useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import deepmerge from 'deepmerge';
import queryString from 'query-string';
import * as Sentry from '@sentry/browser';

import { useSelector } from 'react-redux';
import LOAN_LIMITS from '../../../js/localizations/config/SE/shared/loan-limits';
import { loadState, hasConsent } from '../../../js/utils/form-state';
import FormTypes from '../../../libs/constants/form-types';
import submitApplicationErrorHandler from '../../../js/api/handlers/submit-application-error';
import stripNonNumeric from '../../../js/utils/strip-non-numeric';
import localeData, { LOAN } from '../../../js/localizations/locale-data';
import submitApplicationSuccessHandler from '../../../js/api/handlers/submit-application-success';
import { useOptions } from '../../../context/options';
import {
  useOnSubmitApplicationMutation,
  useOnSubmitMortgageApplicationMutation,
} from '../../../js/store/reducers/api-call';

const { ApplicationForm } = localeData.COMPONENTS;

const Form = ({
  variables,
  prefilledValues,
  type,
  location: windowLocation,
  campaigns,
  partnerForm,
  prefilledApplicationForm,
  ...rest
}) => {
  const search = (windowLocation && windowLocation.search)
  || (typeof window !== 'undefined' ? window.location.search : '');

  const queryParams = useMemo(() => queryString.parse(search), [search]);
  const formState = useSelector((state) => state.form.application);
  const loan = formState?.values || {};

  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const [onSubmitApplication] = useOnSubmitApplicationMutation();
  const [onSubmitMortgageApplication] = useOnSubmitMortgageApplicationMutation();

  const formDefaultValues = useMemo(() => {
    switch (type) {
      case FormTypes.LOAN_CALCULATOR:
        return localeData.LOAN_CALCULATOR_DEFAULT_VALUES;
      case FormTypes.MORTGAGE_LOAN:
        return localeData.MORTGAGE_FORM_DEFAULT_VALUES;
      case FormTypes.NEW_FORM:
      case FormTypes.MERGE_LOAN:
      default:
        return localeData.MERGE_LOAN_FORM_DEFAULT_VALUES;
    }
  }, [type]);

  const persistedFormData = useMemo(() => loadState('application-form'), []);

  const prefillParams = useMemo(() => {
    if (!queryParams.prefill) return null;
    try {
      return JSON.parse(
        atob(queryParams.prefill.replaceAll('_', '/').replaceAll('-', '+')),
      );
    } catch {
      return null;
    }
  }, [queryParams.prefill]);

  const mergedDefaults = useMemo(
    () => deepmerge(formDefaultValues, prefilledValues || {}),
    [formDefaultValues, prefilledValues],
  );

  const objCost = useMemo(() => {
    const value = stripNonNumeric(queryParams.objCost);
    return value > LOAN.UP_TO ? LOAN.UP_TO : value;
  }, [queryParams.objCost]);

  const getRepaymentTime = useCallback(() => {
    if (prefillParams?.repaymentTime != null) {
      return prefillParams.repaymentTime / 12;
    }
    const rt = parseInt(queryParams.repaymentTime, 10);
    if (!Number.isNaN(rt)) return Math.min(Math.max(rt, 1), 20);
    if (persistedFormData?.repaymentTime) {
      return persistedFormData.repaymentTime > 20
        ? persistedFormData.repaymentTime / 12
        : persistedFormData.repaymentTime;
    }
    return formDefaultValues.repaymentTime;
  }, [prefillParams, queryParams.repaymentTime, persistedFormData, formDefaultValues]);

  const getTotalAmount = useCallback(() => {
    const ta = parseInt(queryParams.totalAmount, 10);
    if (prefillParams?.loanAmount != null) return prefillParams.loanAmount;
    if (!Number.isNaN(ta)) {
      return Math.min(Math.max(ta, LOAN_LIMITS.FROM), LOAN_LIMITS.UP_TO);
    }
    return (
      prefilledValues?.totalAmount
      || objCost
      || persistedFormData?.totalAmount
      || formDefaultValues.totalAmount
    );
  }, [prefillParams, queryParams.totalAmount, prefilledValues,
    objCost, persistedFormData, formDefaultValues]);

  const getPhoneNumber = useCallback(() => {
    const phoneStorage = typeof window !== 'undefined' ? sessionStorage.getItem('phoneNumber') : null;
    return (
      prefillParams?.phone
      || prefilledValues?.applicant?.phoneNumber
      || (phoneStorage && phoneStorage !== 'null' ? phoneStorage : null)
      || persistedFormData?.applicant?.phoneNumber
      || null
    );
  }, [prefillParams, prefilledValues, persistedFormData]);

  const getEmail = useCallback(() => {
    const emailStorage = typeof window !== 'undefined' ? sessionStorage.getItem('email') : null;
    return (
      prefillParams?.email
      || prefilledValues?.applicant?.email
      || (emailStorage && emailStorage !== 'null' ? emailStorage : null)
      || persistedFormData?.applicant?.email
      || null
    );
  }, [prefillParams, prefilledValues, persistedFormData]);

  const initialFormValues = useMemo(() => {
    const applicant = {
      ...persistedFormData?.applicant,
      ...mergedDefaults.applicant,
      phoneNumber: hasConsent() ? getPhoneNumber() : null,
      email: hasConsent() ? getEmail() : null,
      nationalId: null,
    };

    const coApplicantData = {
      ...(mergedDefaults.coApplicant || {}),
      ...(persistedFormData?.coApplicant || {}),
    };

    const coApplicant = Object.keys(coApplicantData).length > 0
      ? { ...coApplicantData, nationalId: null }
      : undefined;

    return {
      ...persistedFormData,
      ...mergedDefaults,
      totalAmount: getTotalAmount(),
      repaymentTime: getRepaymentTime(),
      acceptNewsletter:
        persistedFormData?.acceptNewsletter || formDefaultValues.acceptNewsletter,
      applicant,
      coApplicant,
      emailZeroBounceStatus: 'READY',
    };
  }, [
    mergedDefaults,
    persistedFormData,
    getPhoneNumber,
    getEmail,
    getRepaymentTime,
    getTotalAmount,
    formDefaultValues.acceptNewsletter,
  ]);

  const submit = (...args) => {
    const submitMethod = type === FormTypes.MORTGAGE_LOAN
      ? onSubmitMortgageApplication
      : onSubmitApplication;

    return submitMethod(args)
      .unwrap()
      .then(
        submitApplicationSuccessHandler({
          formType: type,
          loan,
          setSubmitSuccess,
        }),
      )
      .catch(({ errorMessage, response, payload }) => {
        Sentry.setTag('origin', 'submitApplication');
        const fingerprint = errorMessage.includes('Applicant with nationalId')
          ? ['submit', 'previousApplication']
          : ['submit', 'onSubmitApplication'];
        Sentry.captureException(errorMessage, {
          stacktrace: true,
          fingerprint,
          extra: { error: response, payload },
        });
        return Promise.reject(response.data);
      })
      .catch(
        submitApplicationErrorHandler({
          formType: type,
          formState,
        }),
      );
  };

  return (
    <ApplicationForm
      form="application"
      submitSuccess={submitSuccess}
      initialValues={initialFormValues}
      destroyOnUnmount={false}
      onSubmit={submit}
      type={type}
      campaigns={campaigns}
      partnerForm={partnerForm}
      prefilledApplicationForm={prefilledApplicationForm}
      {...rest}
    />
  );
};

Form.propTypes = {
  formState: PropTypes.shape(),
  onSubmitApplication: PropTypes.func,
  variables: PropTypes.shape().isRequired,
  campaigns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      info: PropTypes.string,
    }),
  ),
  partnerForm: PropTypes.bool,
  prefilledApplicationForm: PropTypes.bool,
  prefilledValues: PropTypes.shape(),
  type: PropTypes.string,
  location: PropTypes.shape().isRequired,
};

Form.defaultProps = {
  formState: {},
  onSubmitApplication: () => {},
  campaigns: undefined,
  partnerForm: false,
  prefilledApplicationForm: false,
  prefilledValues: {},
  type: FormTypes.MERGE_LOAN,
};

export default (props) => {
  const { optionVariables, optionCampaigns } = useOptions();

  return (
    <Form
      {...props}
      variables={optionVariables}
      campaigns={optionCampaigns.campaigns}
    />
  );
};

export { Form };
